// DropdownFilter.js
import { useState, useEffect } from "react";
import {
  Col,
  Dropdown,
  FloatingLabel,
  Form,
  InputGroup,
  Row,
  Spinner,
} from "react-bootstrap";
import { FieldError } from "react-hook-form";

interface DropdownFilterProps<T> {
  fetchData: (query: string) => Promise<T[]>;
  loading: boolean;
  labelDescription: string;
  labelId?: string;
  getDescription: (item: T) => string;
  getId: (item: T) => number;
  registeredFields: Record<string, any>;
  errorsMessageDescription: FieldError | undefined;
  onSelect?: (item: T | undefined) => void;
  showId?: boolean;
  idColumnWidth?: string;
  idColProps?: { xs?: number; sm?: number; md?: number; lg?: number };
  insert?: boolean;
}

export function DropdownFilterComponent<T>({
  fetchData,
  loading,
  labelDescription,
  labelId,
  getDescription,
  registeredFields,
  errorsMessageDescription,
  onSelect,
  showId = false,
  idColProps = { xs: 6, sm: 6, md: 6, lg: 6 },
  insert = false,
}: DropdownFilterProps<T>): JSX.Element {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<T[]>([]);
  const [typingTimeout, setTypingTimeout] = useState<NodeJS.Timeout | null>(
    null
  );
  const [showDropdown, setShowDropdown] = useState(false);
  const [selected, setSelected] = useState<T>();
  const [inputReadOnly, setInputReadonly] = useState(false);
  const {
    onChange: onChangeId,
    onBlur: onBlurId,
    name: nameId,
    ref: refId,
  } = registeredFields["id"];
  const {
    onChange: onChangeDescription,
    onBlur: onBlurDescription,
    name: nameDescription,
    ref: refDescription,
  } = registeredFields["description"];

  useEffect(() => {
    if (selected) {
      setInputReadonly(true);
    } else {
      setInputReadonly(false);
    }
  }, [selected]);

  useEffect(() => {
    errorsMessageDescription = undefined;
  }, [loading]);

  useEffect(() => {
    if (typingTimeout) {
      clearTimeout(typingTimeout);
    }

    if (query.length >= 3) {
      const timeoutId = setTimeout(() => {
        fetchData(query)
          .then((data) => {
            setResults(data);
            setShowDropdown(true);
          })
          .catch((error) => {
            console.error("Error fetching data:", error);
          });
      }, 1000);

      setTypingTimeout(timeoutId);
    } else {
      setResults([]);
    }

    return () => {
      if (typingTimeout) {
        clearTimeout(typingTimeout);
      }
    };
  }, [query]);

  const OnInputChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { value } = event.target;
    setQuery(value);
    onChangeDescription(event);
  };

  const OnMenuItemClick = (item: T) => {
    setSelected(item);
    setShowDropdown(false);
    onSelect && onSelect(item);
  };

  const OnClick = () => {
    if (inputReadOnly === true) {
      setInputReadonly(false);
    }
  };

  const OnKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Backspace" || event.key === "Delete") {
      setSelected(undefined);
      onSelect && onSelect(undefined);
    } else if (event.key === "Enter") {
      event.preventDefault();
    }
  };

  return (
    <Row className="g-2">
      {showId && (
        <Col {...idColProps}>
          <FloatingLabel
            label={labelId}
            className={showId === true ? "" : "d-none"}
          >
            <Form.Control
              onChange={onChangeId}
              onBlur={onBlurId}
              name={nameId}
              ref={refId}
              placeholder={labelId}
              onKeyDown={OnKeyDown}
            ></Form.Control>
          </FloatingLabel>
        </Col>
      )}
      <Col>
        <InputGroup hasValidation>
          <FloatingLabel label={labelDescription}>
            <Form.Control
              placeholder={labelDescription}
              onChange={(e) => {
                OnInputChange(e);
              }}
              readOnly={inputReadOnly}
              onClick={OnClick}
              onKeyDown={OnKeyDown}
              isInvalid={!!errorsMessageDescription?.message}
              onBlur={onBlurDescription}
              name={nameDescription}
              ref={refDescription}
            ></Form.Control>

            {results.length > 0 && query.length >= 3 && (
              <Dropdown.Menu show={showDropdown}>
                {results.map((item, index) => (
                  <Dropdown.Item
                    key={index}
                    onClick={() => OnMenuItemClick(item)}
                  >
                    {getDescription(item)}
                  </Dropdown.Item>
                ))}
              </Dropdown.Menu>
            )}
            {errorsMessageDescription?.message?.toString() && (
              <Form.Control.Feedback type="invalid">
                {errorsMessageDescription?.message?.toString()}
              </Form.Control.Feedback>
            )}
          </FloatingLabel>
          {loading && (
            <InputGroup.Text>
              <Spinner
                animation="border"
                size="sm"
                role="status"
                aria-hidden="true"
              />
            </InputGroup.Text>
          )}
        </InputGroup>
      </Col>
    </Row>
  );
}
